import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle2, Heart, MapPin, Star, Users, ChevronDown, ChevronUp, Award, Sparkles, Calendar, Instagram, Send, User, Mail, MessageSquare, Crown, Gem, Shield, Clock, Camera, Bed, Car, CalendarCheck, Handshake } from 'lucide-react';
import { testimonials, contactInfo } from '../mock';
import GoogleReviewsWidget from '../components/GoogleReviewsWidget';
import SEO from '../components/SEO';
import { sendToWhatsApp } from '../utils/whatsapp';
import { submitInquiry } from '../utils/leads';

const heroSlides = [
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/fslbolbl_WhatsApp%20Image%202026-03-25%20at%2017.40.49.jpeg', alt: 'Floral mandap with mountain backdrop in Dharamshala' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/detobofu_WhatsApp%20Image%202026-03-25%20at%2017.40.48.jpeg', alt: 'Wedding couple with rose petals at mountain ceremony in Himachal Pradesh' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/cjh9g6tg_WhatsApp%20Image%202026-03-25%20at%2017.56.28.jpeg', alt: 'Haldi ceremony teepee decoration in Dharamshala' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/ut7jo85i_WhatsApp%20Image%202026-03-25%20at%2017.56.29.jpeg', alt: 'Outdoor wedding ceremony with floral mandap in Himachal Pradesh' },
  { src: 'https://images.unsplash.com/photo-1757682065593-9280e1add3d7?w=1280&q=80&fm=webp&auto=format&fit=crop', alt: 'Luxury wedding arch with mountain backdrop in Dharamshala' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/6aqinlm3_WhatsApp%20Image%202026-03-25%20at%2017.40.44.jpeg', alt: 'Luxury wedding favors with Himalayan mountain view' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/8yrf20mz_WhatsApp%20Image%202026-03-25%20at%2017.40.45.jpeg', alt: 'Haldi ceremony decoration with mountain view in Dharamshala' },
];

const galleryImages = [
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/detobofu_WhatsApp%20Image%202026-03-25%20at%2017.40.48.jpeg', title: 'Mountain Wedding Ceremony', category: 'Destination Wedding' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/fslbolbl_WhatsApp%20Image%202026-03-25%20at%2017.40.49.jpeg', title: 'Floral Mandap Setup', category: 'Wedding Décor' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/heore1w2_WhatsApp%20Image%202026-03-25%20at%2017.55.05PPTE4.jpeg', title: 'Grand Banquet Setup', category: 'Event Décor' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/ucz5bue7_WhatsApp%20Image%202026-03-25%20at%2017.53.30%20PPT3.jpeg', title: 'Mehndi Floral Arch', category: 'Wedding Décor' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/d1b5k62w_WhatsApp%20Image%202026-03-25%20at%2017.40.45.jpeg', title: 'Sangeet Night Setup', category: 'Entertainment' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/722ecwmr_WhatsApp%20Image%202026-03-25%20at%2017.40.50.jpeg', title: 'Events Hospitality Desk', category: 'Event Management' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/8yrf20mz_WhatsApp%20Image%202026-03-25%20at%2017.40.45.jpeg', title: 'Haldi Ceremony Décor', category: 'Celebration' },
  { src: 'https://customer-assets.emergentagent.com/job_b2af9d3b-599a-4116-9fa1-3334570e66aa/artifacts/6aqinlm3_WhatsApp%20Image%202026-03-25%20at%2017.40.44.jpeg', title: 'Luxury Wedding Favors', category: 'Wedding Details' },
  { src: 'https://images.unsplash.com/photo-1605553426886-c0a99033fda0?w=600&q=75&fm=webp&auto=format', title: 'Royal Mandap Design', category: 'Wedding Décor' },
];

const Home = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const faqSectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    eventDate: '',
    guestCount: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitInquiry({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        eventType: 'Wedding Inquiry',
        eventDate: formData.eventDate,
        guestCount: formData.guestCount,
        message: formData.message || 'Inquiry from Homepage Form'
      }, 'homepage-form');
    } catch (error) {
      console.error('Error saving inquiry:', error);
    }
    sendToWhatsApp({
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      eventDate: formData.eventDate,
      guestCount: formData.guestCount,
      message: formData.message || 'Interested in wedding planning services'
    }, 'Homepage Inquiry');
    setFormSubmitted(true);
    setIsSubmitting(false);
  };

  // Hero slideshow auto-cycle

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "Why choose Dharamshala for a destination wedding?",
      answer: "Dharamshala offers dramatic Dhauladhar mountain views, a peaceful setting, pleasant weather and beautiful luxury venues — making it one of the most stunning yet serene destinations for a wedding in the Himalayas. It's well connected via Gaggal (Dharamshala) Airport and Pathankot railway station, so your guests can reach comfortably."
    },
    {
      question: "What is the best season for a Dharamshala wedding?",
      answer: "The most popular months are March to June and September to November, when the weather is pleasant and the mountain views are at their clearest. Winter weddings (December–February) are also beautiful for couples who love a crisp, cosy mountain setting."
    },
    {
      question: "Can you arrange accommodation for wedding guests?",
      answer: "Yes. We coordinate guest accommodation across suitable hotels and resorts in and around Dharamshala, along with room blocks, check-ins and a comfortable stay experience for your family and friends."
    },
    {
      question: "Can you manage complete wedding coordination?",
      answer: "Absolutely. We handle everything end-to-end — venue selection, décor and design, vendor coordination, guest logistics, travel and transfers, and full wedding-day management — so you can simply relax and enjoy your celebration."
    },
    {
      question: "How early should we book a Dharamshala wedding?",
      answer: "We recommend reaching out 6–9 months in advance so we can secure your preferred venue and dates, especially during the popular wedding season. For peak dates, the earlier you plan, the better."
    },
    {
      question: "Do you help with travel and local transfers?",
      answer: "Yes — we arrange pickups from Gaggal (Dharamshala) Airport and Pathankot railway station, plus local cabs and transfers between the hotel and wedding venues for you and your guests."
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FFFAF7' }}>
      <SEO
        title="Best Wedding Planner in Dharamshala | Destination Wedding Coordinator & Management"
        description="Dharamshala's top-rated destination wedding planner, coordinator & decorator. Complete wedding planning, management & venues in Himachal Pradesh with transparent pricing. 500+ luxury weddings — get a free quote today."
        keywords="wedding planner in dharamshala, destination wedding coordinator, destination wedding management, wedding planner for destination wedding, dharamshala wedding venues, destination wedding in dharamshala with price, destination wedding himachal pradesh, wedding in himachal pradesh, wedding decorator dharamshala, wedding decor near me, wedding planner in shimla, luxury wedding planner himachal"
        canonical="https://destinationweddingsandevents.in/"
      />

      {/* ═══════════════════════════════════════════════════════════════
          HERO SECTION - Floating Photos on Cream (luxury editorial)
      ═══════════════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden flex items-center justify-center"
        data-testid="hero-section"
        style={{ background: 'linear-gradient(180deg, #FBEEE9 0%, #FFFAF7 45%)', minHeight: '100vh', paddingTop: '7rem', paddingBottom: '6rem' }}
      >
        {/* Floating photo cards (desktop) */}
        {[
          { src: heroSlides[0].src, cls: 'top-[11%] left-[2%] w-40 h-56 rotate-[-6deg]' },
          { src: heroSlides[1].src, cls: 'top-[34%] left-[11%] w-32 h-44 rotate-[5deg]' },
          { src: galleryImages[6].src, cls: 'bottom-[12%] left-[2.5%] w-44 h-60 rotate-[5deg]' },
          { src: heroSlides[2].src, cls: 'bottom-[3%] left-[16%] w-32 h-44 rotate-[-5deg]' },
          { src: heroSlides[4].src, cls: 'top-[12%] right-[2.5%] w-40 h-56 rotate-[6deg]' },
          { src: heroSlides[5].src, cls: 'top-[35%] right-[12%] w-28 h-40 rotate-[-6deg]' },
          { src: galleryImages[2].src, cls: 'bottom-[17%] right-[3%] w-40 h-56 rotate-[-6deg]' },
          { src: heroSlides[3].src, cls: 'bottom-[3%] right-[14%] w-32 h-48 rotate-[5deg]' },
        ].map((p, i) => (
          <div
            key={i}
            className={`absolute hidden lg:block rounded-2xl overflow-hidden ${p.cls}`}
            style={{ border: '5px solid #ffffff', boxShadow: '0 18px 40px rgba(80,50,20,0.18)' }}
            aria-hidden="true"
          >
            <img src={p.src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        ))}

        <div className="relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 text-center" data-testid="hero-content">
          {/* Location pill */}
          <div className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full mb-8" style={{ background: 'rgba(201,169,110,0.10)', border: '1px solid rgba(201,169,110,0.35)' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#C9A96E' }}></span>
            <span className="text-[11px] sm:text-xs tracking-[0.28em] uppercase" style={{ color: '#9a7b43', fontFamily: "'Lato', sans-serif", fontWeight: 600 }}>
              Dharamshala &bull; Himachal Pradesh
            </span>
          </div>

          <h1 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#2b2622', fontSize: 'clamp(34px, 5.5vw, 62px)', lineHeight: 1.08, letterSpacing: '-0.5px' }}>
            Luxury Destination Weddings in
          </h1>
          <div style={{ fontFamily: "'Great Vibes', cursive", color: '#C9A96E', fontSize: 'clamp(58px, 10vw, 116px)', lineHeight: 1.05, marginTop: '0.25rem' }}>
            Dharamshala
          </div>
          <p className="mt-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', color: '#4a4038', fontSize: 'clamp(21px, 3vw, 33px)', fontWeight: 600, lineHeight: 1.3 }}>
            Beautiful Himalayan Weddings, Planned End-to-End
          </p>

          <p className="mt-6 max-w-2xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6f655b', fontSize: 'clamp(18px, 2.4vw, 23px)', lineHeight: 1.6, fontWeight: 500 }}>
            From venue selection and décor to guest accommodation, travel and wedding-day coordination, we plan your Dharamshala wedding from start to finish.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              data-testid="hero-primary-cta"
              onClick={() => window.dispatchEvent(new CustomEvent('open-proposal'))}
              className="group text-white transition-all duration-300 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #C9A96E 0%, #B8943F 100%)', fontFamily: "'Lato', sans-serif", fontSize: '16px', fontWeight: 700, padding: '17px 38px', borderRadius: '50px', boxShadow: '0 10px 30px rgba(201,169,110,0.4)', letterSpacing: '0.5px' }}
            >
              <span className="flex items-center gap-2">
                Get My Free Wedding Proposal
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
            <Link to="/destination-weddings">
              <button
                data-testid="hero-secondary-cta"
                className="transition-all duration-300 hover:scale-105"
                style={{ fontFamily: "'Lato', sans-serif", fontSize: '16px', fontWeight: 600, padding: '17px 38px', borderRadius: '50px', border: '1.5px solid rgba(201,169,110,0.65)', color: '#8a6d3b', background: 'rgba(255,255,255,0.5)' }}
              >
                <span className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" style={{ color: '#C9A96E' }} />
                  Explore Dharamshala Weddings
                </span>
              </button>
            </Link>
          </div>

          <p className="mt-7 text-xs tracking-wide" style={{ color: 'rgba(107,101,91,0.7)', fontFamily: "'Lato', sans-serif" }}>
            No obligation &bull; 100% customized &bull; Dharamshala wedding specialists
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          LOCATION TRUST - Dharamshala Specialists
      ═══════════════════════════════════════════════════════════════ */}
      <section className="relative -mt-12 z-20 px-4 sm:px-6" data-testid="location-trust">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl px-6 py-8 sm:px-10" style={{ border: '1px solid rgba(201,169,110,0.15)' }}>
          <div className="text-center mb-7">
            <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ color: '#C9A96E', fontWeight: 600, fontFamily: "'Lato', sans-serif" }}>Our Specialisation</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Dharamshala Destination Wedding Specialists
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-2xl mx-auto leading-relaxed">
              We focus on weddings in and around Dharamshala — giving you deep local knowledge, the right venues, and a seamless experience for you and your guests in the heart of the Himalayas.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: MapPin, title: 'Dhauladhar Mountain Views', desc: 'Breathtaking Himalayan backdrops' },
              { icon: Crown, title: 'Luxury Resorts & Venues', desc: 'Premium properties around Dharamshala' },
              { icon: Shield, title: 'Peaceful Surroundings', desc: 'Serene, private celebrations' },
              { icon: Users, title: 'Easy Guest Experience', desc: 'Stays, travel & transfers handled' }
            ].map((item, i) => (
              <div key={i} className="text-center p-4 rounded-xl" style={{ background: 'rgba(201,169,110,0.06)', border: '1px solid rgba(201,169,110,0.12)' }}>
                <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'linear-gradient(135deg, #C9A96E, #E8D5A3)' }}>
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <p className="text-sm font-semibold text-gray-800" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</p>
                <p className="text-xs text-gray-500 mt-1 leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          URGENCY BANNER
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-3 px-4" style={{ background: 'linear-gradient(135deg, #2C2C2C 0%, #1a1a1a 100%)' }} data-testid="urgency-banner">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-white text-sm font-medium" style={{ fontFamily: "'Lato', sans-serif" }}>
              Now booking <span style={{ color: '#E8D5A3' }}>2026 &amp; 2027 Dharamshala weddings</span>
            </span>
          </div>
          <button onClick={() => window.dispatchEvent(new CustomEvent('open-proposal'))} data-testid="urgency-cta">
            <span className="text-xs px-4 py-1.5 rounded-full font-semibold cursor-pointer hover:scale-105 transition-transform inline-block" style={{ background: '#C9A96E', color: '#fff' }}>
              Check Availability
            </span>
          </button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          ENQUIRY FORM + INTRO
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, #FFFAF7 0%, #FFF8F2 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            {/* Left - Intro + Why Us Quick */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-8" style={{ background: '#C9A96E' }}></div>
                <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontFamily: "'Lato', sans-serif", fontWeight: 600 }}>About Us</span>
              </div>
              <h2
                className="mb-6"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(28px, 4vw, 44px)',
                  fontWeight: 700,
                  color: '#2C2C2C',
                  lineHeight: 1.2
                }}
              >
                Crafting Timeless
                <span className="block" style={{ color: '#C9A96E' }}>Wedding Experiences</span>
              </h2>
              <p className="text-gray-600 leading-relaxed text-base mb-8" style={{ fontFamily: "'Lato', sans-serif" }}>
                We are a dedicated <strong>Dharamshala destination wedding</strong> planner, turning the majestic Dhauladhar mountains into your personal wedding paradise.
                From intimate mountain ceremonies to grand multi-day celebrations, every detail is orchestrated with precision, elegance, and heart.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: MapPin, title: 'Local Expertise', desc: 'Handpicked Dharamshala venues' },
                  { icon: Award, title: 'Dedicated Team', desc: 'Focused on Dharamshala weddings' },
                  { icon: Shield, title: 'Stress-Free', desc: 'End-to-end planning & execution' },
                  { icon: Sparkles, title: 'Custom Décor', desc: 'Bespoke luxury designs' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(201,169,110,0.06)', border: '1px solid rgba(201,169,110,0.15)' }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #C9A96E, #E8D5A3)' }}>
                      <item.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-800" style={{ fontFamily: "'Lato', sans-serif" }}>{item.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a href={`tel:${contactInfo.phones[0]}`}>
                <button
                  data-testid="intro-call-btn"
                  className="flex items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-semibold transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #C9A96E, #B8943F)', boxShadow: '0 4px 15px rgba(201,169,110,0.3)' }}
                >
                  <Phone className="h-4 w-4" />
                  Talk to a Wedding Expert
                </button>
              </a>
            </div>

            {/* Right - Enquiry Form */}
            <div className="rounded-3xl p-6 md:p-8 shadow-2xl" style={{ background: 'linear-gradient(135deg, #FFFFFF, #FFFAF7)', border: '1px solid rgba(201,169,110,0.2)' }} data-testid="enquiry-form">
              {!formSubmitted ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg" style={{ background: 'linear-gradient(135deg, #C9A96E, #E8D5A3)' }}>
                      <Send className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl text-gray-800" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
                      Get Your Free Proposal
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">We'll call you back within 2 hours</p>
                  </div>

                  <form onSubmit={handleFormSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input type="text" required placeholder="Your Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent transition-all" data-testid="home-form-name" />
                      </div>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input type="tel" required placeholder="Phone *" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent transition-all" data-testid="home-form-phone" />
                      </div>
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="email" placeholder="Email (Optional)" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent transition-all" data-testid="home-form-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input type="text" placeholder="Wedding Date" value={formData.eventDate} onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })} className="w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent transition-all" data-testid="home-form-date" />
                      </div>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <select value={formData.guestCount} onChange={(e) => setFormData({ ...formData, guestCount: e.target.value })} className="w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent transition-all appearance-none" data-testid="home-form-guests">
                          <option value="">Guests</option>
                          <option value="50-100">50-100</option>
                          <option value="100-200">100-200</option>
                          <option value="200-300">200-300</option>
                          <option value="300+">300+</option>
                        </select>
                      </div>
                    </div>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <textarea placeholder="Tell us about your dream wedding..." rows={2} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full pl-10 pr-3 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent transition-all resize-none" data-testid="home-form-message" />
                    </div>
                    <button type="submit" disabled={isSubmitting} className="w-full py-3.5 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-70" style={{ background: 'linear-gradient(135deg, #C9A96E 0%, #B8943F 100%)', boxShadow: '0 6px 20px rgba(201,169,110,0.35)' }} data-testid="home-form-submit">
                      {isSubmitting ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending...</>) : (<><Send className="h-4 w-4" />Get Free Proposal</>)}
                    </button>
                  </form>
                  <p className="text-center text-xs text-gray-400 mt-3">100% Secure &bull; No Spam &bull; Quick Response</p>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
                    <CheckCircle2 className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl text-gray-800 mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>Thank You!</h3>
                  <p className="text-gray-500 text-sm mb-4">Our wedding expert will call you within 2 hours.</p>
                  <button onClick={() => { setFormSubmitted(false); setFormData({ name: '', phone: '', email: '', eventDate: '', guestCount: '', message: '' }); }} className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">Submit Another</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          REAL WEDDINGS IN DHARAMSHALA
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: '#FFFFFF' }} data-testid="real-weddings">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>Our Work</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4.5vw, 52px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Real Weddings in <span style={{ color: '#C9A96E' }}>Dharamshala</span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">A glimpse of the celebrations we've designed against the Dhauladhar mountains</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { src: galleryImages[0].src, type: 'Mountain Wedding Ceremony', desc: 'A floral mandap set against the Dhauladhar ranges.' },
              { src: galleryImages[1].src, type: 'Floral Mandap Décor', desc: 'Fresh-flower mandap styling for an open-air ceremony.' },
              { src: galleryImages[6].src, type: 'Haldi Celebration', desc: 'Vibrant décor with a serene mountain backdrop.' },
              { src: galleryImages[3].src, type: 'Mehndi Function', desc: 'Floral arch and cosy seating for the mehndi.' },
              { src: galleryImages[4].src, type: 'Sangeet Night', desc: 'Elegant stage and lighting for an evening of music.' },
              { src: galleryImages[2].src, type: 'Grand Reception', desc: 'A luxurious banquet setup for the reception.' }
            ].map((w, i) => (
              <div key={i} className="group rounded-2xl overflow-hidden bg-white" style={{ border: '1px solid rgba(201,169,110,0.15)' }}>
                <div className="relative h-64 overflow-hidden">
                  <img src={w.src} alt={`${w.type} in Dharamshala`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: 'rgba(201,169,110,0.95)' }}>{w.type}</div>
                </div>
                <div className="p-5">
                  <p className="flex items-center gap-1.5 text-xs mb-1.5" style={{ color: '#C9A96E' }}><MapPin className="h-3.5 w-3.5" />Dharamshala, Himachal Pradesh</p>
                  <p className="text-gray-500 text-sm leading-relaxed">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/portfolio">
              <button className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-semibold transition-all hover:scale-105" style={{ background: 'transparent', color: '#C9A96E', border: '2px solid #C9A96E' }}>
                View Full Portfolio <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          INSTAGRAM FEED - Live from @destinationweddings.events
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: '#FFFFFF' }} data-testid="instagram-feed">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <Instagram className="h-5 w-5" style={{ color: '#C9A96E' }} />
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4.5vw, 52px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Follow Us on <span style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Instagram</span>
            </h2>
            <a href="https://www.instagram.com/destinationweddings.events" target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-gray-500 hover:text-gray-700 transition-colors text-base" style={{ fontFamily: "'Lato', sans-serif" }}>
              @destinationweddings.events
            </a>
          </div>

          {/* Elfsight Instagram Feed Widget */}
          <div className="mb-10 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" id="instagram-widget-container" data-testid="instagram-grid">
            {galleryImages.slice(0, 6).map((img) => (
              <a key={img.src} href="https://www.instagram.com/destinationweddings.events" target="_blank" rel="noopener noreferrer" className="group relative block aspect-square overflow-hidden rounded-2xl">
                <img src={img.src} alt={img.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <Instagram className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </a>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href="https://www.instagram.com/destinationweddings.events" target="_blank" rel="noopener noreferrer">
              <button className="inline-flex items-center px-8 py-4 text-white font-semibold rounded-full transition-all duration-300 hover:scale-105 text-sm" style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', boxShadow: '0 4px 15px rgba(225,48,108,0.3)' }}>
                <Instagram className="h-5 w-5 mr-2" />
                See More Weddings on Instagram
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          WHY CHOOSE US
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFF8F2 0%, #FFFAF7 100%)' }} data-testid="why-choose-us">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full blur-3xl" style={{ background: 'rgba(201,169,110,0.06)' }}></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl" style={{ background: 'rgba(233,30,140,0.04)' }}></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>Why Us</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4.5vw, 52px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Why Couples Choose <span style={{ color: '#C9A96E' }}>Us</span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto">Complete Dharamshala destination wedding services, handled end-to-end by a dedicated local team</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Heart, title: 'Dharamshala Wedding Planning', desc: 'End-to-end planning tailored to your Dharamshala destination wedding.' },
              { icon: MapPin, title: 'Local Venue & Hotel Coordination', desc: 'We match you with the right Dharamshala venues, resorts and hotels.' },
              { icon: Sparkles, title: 'Décor & Wedding Design', desc: 'Bespoke mandap, floral and stage design for every ceremony.' },
              { icon: Bed, title: 'Guest Accommodation', desc: 'Comfortable stays and room blocks arranged for all your guests.' },
              { icon: Car, title: 'Airport / Railway & Local Transfers', desc: 'Pickups from Gaggal Airport & Pathankot, plus local cabs between venues.' },
              { icon: CalendarCheck, title: 'Wedding Day Management', desc: 'On-ground coordination so every ceremony runs flawlessly.' },
              { icon: Handshake, title: 'Local Vendor Coordination', desc: 'Trusted Dharamshala vendors sourced and managed on your behalf.' }
            ].map((item, i) => (
              <div key={i} className="group p-6 rounded-2xl transition-all duration-300 hover:shadow-xl" style={{ background: 'white', border: '1px solid rgba(201,169,110,0.15)' }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, rgba(201,169,110,0.12), rgba(201,169,110,0.05))' }}>
                  <item.icon className="h-7 w-7" style={{ color: '#C9A96E' }} />
                </div>
                <h3 className="mb-2 text-base font-semibold text-gray-800" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          OUR SERVICES
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: '#FFFFFF' }} data-testid="our-services">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>Services</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4.5vw, 52px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Crafting Your <span style={{ color: '#C9A96E' }}>Dream Celebration</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Destination Weddings', desc: 'Complete planning for luxury mountain weddings with stunning Himalayan backdrops and exclusive venues.', img: 'https://images.unsplash.com/photo-1727425383452-2be55354f06e?w=500&q=75&fm=webp&auto=format&fit=crop', link: '/destination-weddings', icon: Heart },
              { title: 'Wedding Décor', desc: 'Bespoke mandap designs, floral arrangements, stage decoration, and complete venue transformation.', img: 'https://images.unsplash.com/photo-1745573673583-a51f665ae48e?w=500&q=75&fm=webp&auto=format&fit=crop', link: '/wedding-decor', icon: Sparkles },
              { title: 'Corporate Events', desc: 'Professional event management for conferences, team retreats, and business celebrations in Dharamshala.', img: 'https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?w=500&q=75&fm=webp&auto=format&fit=crop', link: '/corporate-events', icon: Users },
              { title: 'Private Celebrations', desc: 'Haldi, Mehndi, Sangeet, Cocktail Night, Reception — every ceremony executed with perfection.', img: 'https://images.unsplash.com/photo-1587012521796-6359d3678f2a?w=500&q=75&fm=webp&auto=format&fit=crop', link: '/complete-celebration', icon: Crown }
            ].map((service, i) => (
              <Link to={service.link} key={i} className="group">
                <div className="relative overflow-hidden rounded-2xl h-80 mb-4" style={{ border: '1px solid rgba(201,169,110,0.15)' }}>
                  <img src={service.img} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
                  <div className="absolute top-4 right-4 w-12 h-12 rounded-xl flex items-center justify-center backdrop-blur-md" style={{ background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(201,169,110,0.3)' }}>
                    <service.icon className="h-6 w-6" style={{ color: '#C9A96E' }} />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{service.title}</h3>
                    <div className="w-10 h-0.5 mt-2 rounded-full" style={{ background: '#C9A96E' }}></div>
                  </div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed px-1">{service.desc}</p>
                <p className="mt-2 text-sm font-semibold flex items-center gap-1 px-1 group-hover:gap-2 transition-all" style={{ color: '#C9A96E' }}>
                  Learn More <ArrowRight className="h-4 w-4" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          DHARAMSHALA WEDDING VENUES
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, #FFFAF7 0%, #FFF8F2 100%)' }} data-testid="dharamshala-venues">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>Venues</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4.5vw, 50px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Beautiful Wedding Venues Around <span style={{ color: '#C9A96E' }}>Dharamshala</span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">From luxury mountain resorts to open-air venues framed by the Dhauladhar range — we help you find the perfect setting.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { src: heroSlides[4].src, title: 'Luxury Mountain Resorts', desc: 'Full-service resorts with grand banquet halls and lawns.' },
              { src: galleryImages[5].src, title: 'Heritage & Boutique Hotels', desc: 'Characterful properties for intimate celebrations.' },
              { src: heroSlides[3].src, title: 'Open-Air Mountain Venues', desc: 'Ceremonies framed by the Dhauladhar ranges.' },
              { src: heroSlides[1].src, title: 'Valley & Garden Settings', desc: 'Lush green venues a short drive from Dharamshala.' }
            ].map((v, i) => (
              <div key={i} className="group relative h-72 rounded-2xl overflow-hidden">
                <img src={v.src} alt={`${v.title} near Dharamshala`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.72), transparent 60%)' }}></div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <h3 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{v.title}</h3>
                  <p className="text-white/85 text-xs mt-1 leading-snug">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/destination-weddings">
              <button className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white text-sm font-semibold transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #C9A96E, #B8943F)', boxShadow: '0 8px 30px rgba(201,169,110,0.35)' }}>
                Explore Dharamshala Weddings <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          HOW IT WORKS
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: '#FFFFFF' }} data-testid="how-it-works">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>How It Works</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4.5vw, 50px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Your Dharamshala Wedding, <span style={{ color: '#C9A96E' }}>Step by Step</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {[
              { step: '01', title: 'Tell Us Your Wedding Vision', desc: 'Share your dates, guest count and dream.' },
              { step: '02', title: 'Get Your Custom Dharamshala Proposal', desc: 'A personalised plan and pricing.' },
              { step: '03', title: 'Finalise Venue & Wedding Design', desc: 'Lock your venue, décor and theme.' },
              { step: '04', title: 'We Coordinate Vendors & Guests', desc: 'Vendors, stays, travel — all handled.' },
              { step: '05', title: 'Enjoy Your Wedding', desc: 'Celebrate while we manage the day.' }
            ].map((s, i) => (
              <div key={i} className="text-center p-5 rounded-2xl" style={{ background: '#FFFAF7', border: '1px solid rgba(201,169,110,0.15)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold" style={{ background: 'linear-gradient(135deg, #C9A96E, #B8943F)', fontFamily: "'Playfair Display', serif" }}>{s.step}</div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{s.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          TESTIMONIALS
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, #FFF8F2 0%, #FFFAF7 100%)' }} data-testid="testimonials">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>Testimonials</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4.5vw, 52px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              What Our <span style={{ color: '#C9A96E' }}>Couples Say</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-white p-7 rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl" style={{ border: '1px solid rgba(201,169,110,0.12)' }}>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" style={{ color: '#C9A96E' }} />
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid rgba(201,169,110,0.12)' }}>
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    <img src={`${t.image}?w=80&q=60&fm=webp`} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-800" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</p>
                    <p className="text-xs text-gray-400">{t.event}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Widget */}
      <GoogleReviewsWidget />

      {/* ═══════════════════════════════════════════════════════════════
          FAQ SECTION
      ═══════════════════════════════════════════════════════════════ */}
      <section ref={faqSectionRef} id="faq-section" className="py-20 px-4 sm:px-6" style={{ background: '#FFFFFF' }} data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>FAQs</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 700, color: '#2C2C2C' }}>
              Frequently Asked <span style={{ color: '#C9A96E' }}>Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden transition-shadow hover:shadow-lg" style={{ border: '1px solid rgba(201,169,110,0.12)' }}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-50/50 transition-colors">
                  <h3 className="text-gray-800 font-semibold text-base pr-4">{faq.question}</h3>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${openFaq === index ? 'text-white' : ''}`} style={{ background: openFaq === index ? '#C9A96E' : 'rgba(201,169,110,0.1)', color: openFaq === index ? 'white' : '#C9A96E' }}>
                    {openFaq === index ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #FFFAF7, #FFFFFF)', borderTop: '1px solid rgba(201,169,110,0.1)' }}>
                    <p className="text-gray-600 leading-relaxed text-sm">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          WEDDING PLANNING PROPOSAL
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6" style={{ background: 'linear-gradient(180deg, #FFF8F2 0%, #FFFAF7 100%)' }} data-testid="wedding-proposal-section">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: '#C9A96E', fontWeight: 600 }}>Our Proposal</span>
              <div className="h-px w-12" style={{ background: '#C9A96E' }}></div>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4.5vw, 52px)', fontWeight: 700, color: '#2C2C2C', lineHeight: 1.2 }}>
              Wedding Planning <span style={{ color: '#C9A96E' }}>&amp; Management</span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: '18px' }}>
              "Memorable Events don't Just Happen. They happen to be Our Business"
            </p>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-sm">
              Thank you for considering Destination Weddings and Events. We are dedicated to creating an unforgettable and seamless experience with every detail perfectly executed.
            </p>
          </div>

          {/* Planning Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            {[
              {
                title: 'Pre-Wedding Planning',
                items: ['Initial consultation & vision planning', 'Budget creation & management', 'Venue research & site visits', 'Vendor shortlisting & negotiations', 'Timeline & checklist creation', 'Guest accommodation coordination']
              },
              {
                title: 'Vendor Management',
                items: ['Caterer selection & menu planning', 'Photographer & videographer booking', 'Makeup artist & styling team', 'Entertainment & music coordination', 'Florist & décor team management', 'Contract negotiations & payments']
              },
              {
                title: 'Event Day Management',
                items: ['Full day-of coordination', 'Vendor arrival & setup supervision', 'Timeline execution & management', 'Emergency handling & problem solving', 'Guest coordination & assistance', 'Ceremony & reception flow management']
              },
              {
                title: 'Additional Services',
                items: ['Save the Date & invitation management', 'RSVP tracking & guest list management', 'Transport & logistics coordination', 'Welcome bags & guest amenities', 'Post-wedding brunch arrangements', 'Honeymoon planning assistance']
              }
            ].map((service, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow" style={{ border: '1px solid rgba(201,169,110,0.12)' }}>
                <h3 className="text-base font-semibold text-gray-800 mb-4 pb-3" style={{ fontFamily: "'Playfair Display', serif", borderBottom: '2px solid #C9A96E' }}>{service.title}</h3>
                <ul className="space-y-2.5">
                  {service.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#C9A96E' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Pricing Packages */}
          <div className="text-center mb-10">
            <h3 className="text-2xl text-gray-800 mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
              Investment <span style={{ color: '#C9A96E' }}>Packages</span>
            </h3>
            <p className="text-gray-500 text-sm">Professional wedding planning services</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-4xl mx-auto">
            {/* Package 1 */}
            <div className="bg-white rounded-2xl p-7 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden" style={{ border: '1px solid rgba(201,169,110,0.2)' }}>
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #C9A96E, #E8D5A3)' }}></div>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A96E', fontWeight: 600 }}>Package 1</p>
              <h4 className="text-lg font-semibold text-gray-800 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Wedding Planning &amp; Management</h4>
              <p className="text-gray-400 text-xs mb-4">For up to 100-150 guests &bull; 2 days of celebrations</p>
              <p className="text-3xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>
                ₹2,50,000 - ₹3,00,000
              </p>
              <p className="text-gray-400 text-xs mb-5">+ GST</p>
              <Link to="/wedding-proposal">
                <button className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-all hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #C9A96E, #B8943F)' }}>
                  Get This Package
                </button>
              </Link>
            </div>

            {/* Package 2 */}
            <div className="bg-white rounded-2xl p-7 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden" style={{ border: '2px solid #C9A96E' }}>
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #C9A96E, #E8D5A3, #C9A96E)' }}></div>
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: '#C9A96E' }}>Popular</div>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A96E', fontWeight: 600 }}>Package 2</p>
              <h4 className="text-lg font-semibold text-gray-800 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Wedding Planning &amp; Management</h4>
              <p className="text-gray-400 text-xs mb-4">For up to 200-250 guests &bull; 2 days of celebrations</p>
              <p className="text-3xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>
                ₹3,50,000 - ₹4,00,000
              </p>
              <p className="text-gray-400 text-xs mb-5">+ GST</p>
              <Link to="/wedding-proposal">
                <button className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-all hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #C9A96E, #B8943F)' }}>
                  Get This Package
                </button>
              </Link>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-4 mb-10">Additional day cost: ₹50,000 per day</p>

          {/* Additional Info */}
          <div className="max-w-4xl mx-auto space-y-4 mb-12">
            <div className="bg-white rounded-xl p-5 flex items-start gap-3" style={{ border: '1px solid rgba(201,169,110,0.12)' }}>
              <span className="text-lg">⚠️</span>
              <div>
                <p className="text-sm font-semibold text-gray-700">Important Note</p>
                <p className="text-xs text-gray-500 mt-1">The above Wedding Planning and Management charges are valid only when décor services are also booked with us. If you choose another décor vendor, our planning fee starts at ₹5,00,000/- onwards.</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 flex items-start gap-3" style={{ border: '1px solid rgba(201,169,110,0.12)' }}>
              <span className="text-lg">📌</span>
              <div>
                <p className="text-sm font-semibold text-gray-700">Meetings &amp; Venue Visits</p>
                <p className="text-xs text-gray-500 mt-1">Package includes up to three complimentary meetings or venue visits. Each additional visit: ₹10,000 per visit.</p>
              </div>
            </div>
          </div>

          {/* Add-on: RSVP */}
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-6 mb-12 shadow-md" style={{ border: '1px solid rgba(201,169,110,0.15)' }}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#C9A96E', fontWeight: 600 }}>Add-on Optional</p>
                <h4 className="text-base font-semibold text-gray-800 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>RSVP Management</h4>
                <ul className="space-y-2">
                  {[
                    'Sending out Save the Dates and static invites',
                    'Managing guest responses and dietary requirements',
                    'Coordinating stay arrangements and guest lists',
                    'Maintaining arrival and departure logistics sheet'
                  ].map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#C9A96E' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 mt-3 italic">Please note - Any increase in guest count will be charged at an incremental rate of INR 50,000 per 50 guests and so on.</p>
              </div>
              <p className="text-xl font-bold whitespace-nowrap md:text-right" style={{ fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>₹1,00,000 <span className="text-xs text-gray-400 font-normal block">for up to 100 guests</span></p>
            </div>
          </div>

          {/* Payment Schedule */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl text-gray-800 text-center mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
              Payment <span style={{ color: '#C9A96E' }}>Schedule</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '1', title: 'First Installment', pct: '50%', when: 'At the time of Booking', note: 'Non-refundable advance' },
                { step: '2', title: 'Second Installment', pct: '35%', when: '5 months prior to Event', note: '' },
                { step: '3', title: 'Final Payment', pct: '15%', when: '20 days before Event', note: '' }
              ].map((p, i) => (
                <div key={i} className="bg-white rounded-xl p-5 text-center shadow-sm" style={{ border: '1px solid rgba(201,169,110,0.12)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 text-white text-sm font-bold" style={{ background: '#C9A96E' }}>{p.step}</div>
                  <p className="text-sm font-semibold text-gray-700">{p.title}</p>
                  <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>{p.pct}</p>
                  <p className="text-xs text-gray-500 mt-1">{p.when}</p>
                  {p.note && <p className="text-xs mt-1" style={{ color: '#E91E8C' }}>{p.note}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4 text-sm">Interested? Let's discuss your dream wedding!</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://wa.me/918679333354?text=Hi%2C%20I%20viewed%20your%20wedding%20proposal%20and%20would%20like%20to%20discuss%20my%20wedding%20plans." target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-3.5 rounded-full text-white text-sm font-semibold transition-all hover:scale-105 flex items-center gap-2" style={{ background: '#25D366', boxShadow: '0 4px 15px rgba(37,211,102,0.3)' }}>
                  <MessageSquare className="h-4 w-4" />
                  Chat on WhatsApp
                </button>
              </a>
              <a href="tel:+918679333354">
                <button className="px-8 py-3.5 rounded-full text-sm font-semibold transition-all hover:scale-105 flex items-center gap-2" style={{ background: 'transparent', color: '#C9A96E', border: '2px solid #C9A96E' }}>
                  <Phone className="h-4 w-4" />
                  Call +91 86793 33354
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          FINAL CTA - Emotional
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_pretty-planet-travel/artifacts/c2co11fh_IMG_1458.JPG" alt="Luxury wedding backdrop" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(44,28,16,0.55), rgba(160,130,80,0.40))' }}></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-white/30"></div>
            <Heart className="h-6 w-6" style={{ color: '#E8D5A3' }} />
            <div className="h-px w-12 bg-white/30"></div>
          </div>
          <h2 className="text-white mb-6" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 700, lineHeight: 1.2 }}>
            Plan Your Dream Wedding in
            <span className="block" style={{ color: '#E8D5A3' }}>Dharamshala</span>
          </h2>
          <p className="text-white/80 mb-10 text-lg max-w-2xl mx-auto leading-relaxed">
            Tell us your date, guest count and wedding vision — we'll prepare a personalised proposal tailored to your Dharamshala celebration.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button data-testid="final-cta-btn" onClick={() => window.dispatchEvent(new CustomEvent('open-proposal'))} className="px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all" style={{ background: '#C9A96E', color: '#FFFFFF', boxShadow: '0 8px 30px rgba(201,169,110,0.4)' }}>
              Get Free Proposal Now
              <ArrowRight className="inline h-5 w-5 ml-2" />
            </button>
            <a href={`tel:${contactInfo.phones[0]}`}>
              <button className="px-10 py-5 backdrop-blur-sm text-white rounded-full font-semibold hover:bg-white/20 transition-all border border-white/30 flex items-center">
                <Phone className="h-5 w-5 mr-2" />
                Talk to Expert
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SEO FOOTER TEXT
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 px-4 sm:px-6" style={{ background: '#FFFAF7' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="mb-4 text-gray-800" style={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', fontWeight: 600 }}>
            Premier <span style={{ color: '#C9A96E' }}>Wedding Planner in Dharamshala</span>
          </h3>
          <div className="w-16 h-0.5 mx-auto rounded-full mb-6" style={{ background: '#C9A96E' }}></div>
          <p className="text-gray-500 leading-relaxed text-sm max-w-4xl mx-auto mb-6">
            Destination Weddings & Events is a specialist <strong>Dharamshala destination wedding</strong> planner, <strong>coordinator</strong> and <strong>event organiser</strong>. We handle complete <strong>wedding planning and management</strong> in Dharamshala, Himachal Pradesh — from handpicked <strong>Dharamshala wedding venues</strong> and luxury décor to mandap design, floral arrangements, professional sound with JBL and RCF systems, guest accommodation, transfers, and end-to-end management from Haldi to Reception, with transparent pricing. Planning a <strong>destination wedding in Dharamshala</strong>? Contact us for a personalised proposal.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Wedding Planner in Dharamshala', 'Destination Wedding Coordinator', 'Destination Wedding Management', 'Dharamshala Wedding Venues', 'Best Wedding Planner Dharamshala', 'Wedding Decorator Dharamshala', 'Wedding Decor Near Me', 'Wedding Planner in Shimla', 'Corporate Event Planner'].map((tag, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: 'rgba(201,169,110,0.08)', color: '#C9A96E', border: '1px solid rgba(201,169,110,0.15)' }}>{tag}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
